import React, { Fragment, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.scss';
import classnames from 'classnames';

import colorVariables from '@densityco/ui/variables/colors.json';

import BackButton from '../../BackButton';
import Loading from '../../Loading';
import ErrorState from '../../ErrorState';
import FormLabel from '../../FormLabel';
import AddSensorSidebar from '../AddSensorSidebar';

import {
  AppBar,
  AppBarTitle,
  AppBarSection,
  AppBarContext,
  Button,
  Dialogger,
  InputBox,
  Skeleton,
  Icons,
} from '@densityco/ui';
import Modal from '../../Modal';
import HorizontalForm from '../../HorizontalForm';
import Tooltip from '../../Tooltip';

import MultiPointInstallationEditStore from '../../../rx-stores/multi-point-installation-edit';
import {
  MultiPointInstallationEditAction,
  MultiPointInstallationEditActionTypes,
} from '../../../types/multi-point-installation-edit';
import { ResourceStatus } from '../../../types/resource';

import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import { DEFAULT_MAX_X_OFFSET_MULTIPLIER, DEFAULT_SPAN_MS } from '../../../utils/multi-point-installations';

import SensorRearranger from '../SensorRearranger';

const FLOAT_REGEX = /^[0-9]+\.?[0-9]*$/;

const MultiPointInstallationEditPage: React.FunctionComponent<{}> = () => {
  const state = useRxStore(MultiPointInstallationEditStore);
  const dispatch = useRxDispatch();
  const { organizationIdOrAll, mpiId } = useParams();

  const [showSensorSidebar, setShowSensorSidebar] = useState(false);
  const [showEditConfiguration, setShowEditConfiguration] = useState(false);

  useEffect(() => {
    dispatch({
      type:
        MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_CHANGE_PAGE,
      id: mpiId,
    });
  }, []);

  switch (state.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton
                width={150}
                height={20}
                color={colorVariables.gray300}
              />
            </AppBarTitle>
          </AppBar>
          <Loading />
        </Fragment>
      );
    case ResourceStatus.ERROR:
      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
            </AppBarTitle>
          </AppBar>
          <ErrorState error={state.resource.error} />
        </Fragment>
      );
    case ResourceStatus.COMPLETE:
      const nameInvalid = state.resource.data.name.length === 0;
      const heightInvalid = state.resource.data.height.length === 0 &&
        !FLOAT_REGEX.test(state.resource.data.height);

      const spanMSInvalid =
        state.resource.data.spanMS.length !== 0 && /* unset case */
        !FLOAT_REGEX.test(state.resource.data.spanMS);

      const maxXOffsetMultiplierInvalid =
        state.resource.data.maxXOffsetMultiplier.length !== 0 && /* unset case */
        !FLOAT_REGEX.test(state.resource.data.maxXOffsetMultiplier);

      // A multi point installation requires at least 2 sensors.
      const sensorsInvalid = state.resource.data.sensors.length < 2;

      const formInvalid =
        nameInvalid ||
        heightInvalid ||
        spanMSInvalid ||
        maxXOffsetMultiplierInvalid ||
        sensorsInvalid;

      return (
        <Fragment>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <span data-test-id="mpi-edit-title">
                {state.resource.data.name || 'Edit MPI'}
              </span>
            </AppBarTitle>
          </AppBar>

          <div className={styles.wrapper}>
            <div className={styles.configurationContainer}>
              <AppBarContext.Provider value="CARD_HEADER">
                <AppBar>
                  <AppBarTitle>Configuration</AppBarTitle>
                </AppBar>
              </AppBarContext.Provider>

              <div className={styles.configurationBody}>
                <div className={styles.configurationBodyRow}>
                  <div className={styles.configurationBodyCell}>
                    <FormLabel
                      label="Name"
                      htmlFor="name"
                      input={
                        <InputBox
                          type="text"
                          id="name"
                          placeholder={'ex: "Main Entry", "Stairwell A"'}
                          width="100%"
                          value={state.resource.data.name}
                          invalid={nameInvalid}
                          onChange={e =>
                            dispatch({
                              type:
                                MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_NAME,
                              name: e.target.value,
                            })
                          }
                          data-test-id="mpi-edit-name"
                        />
                      }
                    />
                  </div>
                  <div className={styles.configurationBodyCell}>
                    <FormLabel
                      label="Height (in)"
                      htmlFor="height"
                      input={
                        <InputBox
                          type="text"
                          placeholder="ex: 120 inches"
                          width="100%"
                          id="height"
                          value={state.resource.data.height}
                          invalid={heightInvalid}
                          onChange={e =>
                            dispatch({
                              type:
                                MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_HEIGHT,
                              height: e.target.value,
                            })
                          }
                          data-test-id="mpi-edit-height"
                        />
                      }
                    />
                  </div>
                </div>
                <div className={styles.configurationBodyRow}>
                  <div className={styles.configurationBodyCell}>
                    <FormLabel
                      label={
                        <Tooltip
                          contents={
                            <Fragment>
                              The window of time in which the system considers an event from
                              neighboring sensors to be the same event.
                              <br />
                              <br />
                              If neighboring sensors have corresponding algo events that are
                              slightly offset, that's a sign that this value needs to be increased.
                              <br />
                              <br />
                              In practice, this is mainly needed to account for Sensor clock drift.
                            </Fragment>
                          }
                          target={
                            <Fragment>
                              Span (ms)
                              <div className={styles.info}>
                                <Icons.Info width={16} height={16} />
                              </div>
                            </Fragment>
                          }
                        />
                      }
                      htmlFor="span-ms"
                      input={
                        <InputBox
                          type="text"
                          placeholder={`default: ${DEFAULT_SPAN_MS}ms`}
                          width="100%"
                          id="span-ms"
                          value={state.resource.data.spanMS}
                          invalid={spanMSInvalid}
                          onChange={e =>
                            dispatch({
                              type:
                                MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_SPAN_MS,
                              spanMS: e.target.value,
                            })
                          }
                          data-test-id="mpi-edit-span-ms"
                        />
                      }
                    />
                  </div>
                  <div className={styles.configurationBodyCell}>
                    <FormLabel
                      label={
                        <Tooltip
                          contents={
                            <Fragment>
                              A means of extending the overlapping region between two units.
                              <br />
                              <br />
                              If an event is not being deduplicated which should be and it is close
                              or just outside of the green region, then increasing that multiplier
                              should cause them to deduplicate.
                            </Fragment>
                          }
                          target={
                            <Fragment>
                              Max X Offset Multiplier
                              <div className={styles.info}>
                                <Icons.Info width={16} height={16} />
                              </div>
                            </Fragment>
                          }
                        />
                      }
                      htmlFor="max-x-offset-multiplier"
                      input={
                        <InputBox
                          type="text"
                          placeholder={`default: ${DEFAULT_MAX_X_OFFSET_MULTIPLIER}`}
                          width="100%"
                          id="max-x-offset-multiplier"
                          value={state.resource.data.maxXOffsetMultiplier}
                          invalid={maxXOffsetMultiplierInvalid}
                          onChange={e =>
                            dispatch({
                              type:
                                MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_MAX_X_OFFSET_MULTIPLIER,
                              maxXOffsetMultiplier: e.target.value,
                            })
                          }
                          data-test-id="mpi-edit-max-x-offset-multiplier"
                        />
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.rearrangerContainer}>
              <SensorRearranger
                positions={state.resource.data.sensors}
                onAddSensor={() => setShowSensorSidebar(true)}
                onDeleteSensor={serialNumber =>
                  dispatch({
                    type:
                      MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_REMOVE_SENSOR,
                    serialNumber,
                  })
                }
                onReorderSensors={serialNumbers =>
                  dispatch({
                    type:
                      MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_SENSOR_ORDER,
                    serialNumbers,
                  })
                }
                onSetSpacingRight={(serialNumber, spacingRight) => {
                  dispatch({
                    type:
                      MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SET_SPACING_RIGHT,
                    serialNumber,
                    spacingRight,
                  });
                }}
              />
            </div>

            <div className={styles.deleteContainer}>
              <div className={styles.deleteLeft}>
                <span className={styles.deleteHeader}>
                  Delete this Multi Point Installation
                </span>
                Once it's deleted, it will be gone forever. Please be certain.
              </div>
              <div className={styles.deleteRight}>
                <Button
                  type="danger"
                  variant="underline"
                  onClick={async () => {
                    const confirmed = await Dialogger.confirm({
                      prompt: 'Are you sure you want to delete this MPI?',
                      confirmText: 'Delete',
                    });
                    if (!confirmed) {
                      return;
                    }

                    dispatch({
                      type:
                        MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_DELETE,
                      organizationIdOrAll,
                    });
                  }}
                  data-test-id="mpi-edit-delete"
                >
                  Delete this MPI
                </Button>
              </div>
            </div>
          </div>

          <AppBar>
            <AppBarSection />
            <AppBarSection>
              <HorizontalForm>
                <Button
                  variant="underline"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
                <Button
                  variant="filled"
                  disabled={state.saveButtonDisabled || formInvalid}
                  onClick={() => {
                    dispatch({
                      type:
                        MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_SUBMIT,
                      organizationIdOrAll,
                    });
                  }}
                  data-test-id="mpi-edit-save"
                >
                  Save
                </Button>
              </HorizontalForm>
            </AppBarSection>
          </AppBar>

          {/* ADD SENSORS SIDEBAR */}
          <AddSensorSidebar
            open={showSensorSidebar}
            onDismiss={() => setShowSensorSidebar(false)}
            onSubmit={sensors => {
              dispatch({
                type:
                  MultiPointInstallationEditActionTypes.MULTI_POINT_INSTALLATION_EDIT_ADD_SENSORS,
                sensors,
              });
              setShowSensorSidebar(false);
            }}
          />
        </Fragment>
      );
  }
};

export default MultiPointInstallationEditPage;
